/**
 * glass-condense.js — the "glass condenses" arrival gesture.
 *
 * The arrival vocabulary for GLASS material (DESIGN Principle 18): where struck
 * metal has its press/sheen language and inscribed marks have the etch, glass
 * arrives as weather — the pane condenses into being as fogged glass (a warm mist
 * overlay), then the mist clears and the content sharpens into view underneath:
 * "breath on a window pane fading", calm, no flash (SCREENS Decision 4 rules those
 * out). Settled as the start screen's reference-strip arrival, Andre's keeper
 * verdict after four prototype rounds (start-screen-entrance-animations,
 * 2026-08-18); graduated here from game-start/ at its second and third consumers
 * (glass-condense-adoption, 2026-08-31). Production consumers now: the start
 * screen's entrance — the strip's TILES group and the menu button beside it
 * (game-start-entrance.js) — and the result strip's glass row
 * (animation/result-strip-condense.js).
 *
 * Built compositor-only per docs/PERFORMANCE.md § never animate filter at
 * multiplicity: the fog is a static gradient overlay animated by OPACITY alone
 * (the ink-wash replacement pattern), never a per-tile blur tween — the tiles
 * already carry a live backdrop-filter at rest, which this gesture never touches.
 * Measured on-device 2026-08-31 (Pixel 5, device-ab.js, warm base runs): both
 * production windows overlap their pre-adoption recorded bands within
 * run-to-run spread — `entrance` (strip + menu button) 750–904ms stall over
 * 975–1006 frames vs the recorded 790–895 over ~1005; `roundend` (result
 * strip) 448/512ms vs the recorded 503/542, at or below its band
 * (docs/PERFORMANCE.md carries both baselines). A cross-session band read,
 * not an interleaved A/B: it supports "no regression detectable", not a
 * stronger claim.
 *
 * Kit idiom: exports the GLASS_CONDENSE_DEFAULTS knob bag (lab-tunable — the
 * Glass Condense animation-lab tab), addGlassCondense merges caller overrides.
 */
import { gsap } from "../../vendor/gsap.js";

export const GLASS_CONDENSE_DEFAULTS = {
  paneInS: 0.35, // play-test: the fogged pane condensing into being
  contentInS: 0.5, // play-test: icon + label sharpening up
  fogClearS: 1.5, // play-test: the mist wiping off the pane — deliberately long, reads as weather; on the start screen it's among the last things still moving (Andre, lab round 2026-08-18)
  contentAtFrac: 0.382, // play-test: content starts at 1/φ² of the pane-in — earlier than the golden cut, so the content leads while the mist is still thick (Andre's pick over 1/φ)
  contentRisePx: 8, // play-test: the content's settle rise — from the pane's bottom edge, not a mid-air fade
  staggerS: 0.13, // play-test: left-to-right travel across a row of panes
  fogOpacity: 0.6, // play-test: the mist's peak density (scales all three gradient stops) — a subtler breath than the first cut's 0.85
  fogWarmth: 0.4, // play-test: 0 = cool morning mist, 1 = warm candlelit breath — cooler reads more like glass
};

// The fog's gradient stops as a warmth lerp: cool morning mist ↔ the warm-white
// breath that harmonizes with the tiles' heritage-glass body (skat.css .icon-tile).
const FOG_STOPS = [
  { cool: [240, 246, 250], warm: [255, 246, 230], alpha: 0.82 },
  { cool: [226, 234, 240], warm: [244, 228, 204], alpha: 0.55 },
  { cool: [210, 222, 230], warm: [230, 210, 182], alpha: 0.32 },
];

function fogGradient({ fogWarmth, fogOpacity }) {
  const stops = FOG_STOPS.map(({ cool, warm, alpha }) => {
    const c = cool.map((v, i) => Math.round(v + (warm[i] - v) * fogWarmth));
    return `rgba(${c[0]},${c[1]},${c[2]},${(alpha * fogOpacity).toFixed(3)})`;
  });
  return `radial-gradient(130% 130% at 30% 15%, ${stops[0]} 0%, ${stops[1]} 45%, ${stops[2]} 100%)`;
}

// The fog pane: a static mist gradient over the tile, opacity-driven only.
function buildFogPane(tile, config) {
  const fog = document.createElement("div");
  fog.className = "gs-glass-fog"; // ephemeral animation DOM, shed at the end
  fog.style.cssText =
    "position:absolute; inset:0; border-radius:inherit; pointer-events:none; z-index:2; " +
    `background: ${fogGradient(config)};`;
  tile.appendChild(fog);
  return fog;
}

/**
 * Weave the glass condense onto `master` for every element in `elements` (any
 * iterable — a NodeList straight off querySelectorAll, or an array), staggered in
 * ELEMENT order from `at`: element order is stagger order, so a caller wanting the
 * document's left-to-right sweep passes document order (which querySelectorAll
 * already gives). Stages everything hidden immediately (build time), so the panes
 * never sit visible before their beat. Overrides merge over GLASS_CONDENSE_DEFAULTS.
 *
 * Returns a residue array shaped {svg, group, contents} — the fog rides the `svg`
 * slot. That shape is deliberately IDENTICAL to game-start-toggles-etch.js's, so the
 * start screen's entrance can merge both groups' residue into one list and shed it
 * through shedTogglesEtch in a single call (a documented contract on both sides —
 * see that module's header). Every other consumer sheds via shedGlassCondense below.
 */
export function addGlassCondense(master, elements, at, overrides = {}) {
  const config = { ...GLASS_CONDENSE_DEFAULTS, ...overrides };
  return [...elements].map((tile, i) => {
    const contents = [...tile.children];
    const fog = buildFogPane(tile, config);
    gsap.set(tile, { autoAlpha: 0 });
    gsap.set(contents, { autoAlpha: 0, y: config.contentRisePx });
    const start = at + i * config.staggerS;
    // The pane condenses: tile (glass body + fog child) breathes in as one misted pane.
    master.to(
      tile,
      { autoAlpha: 1, duration: config.paneInS, ease: "power1.out" },
      start,
    );
    // The content sharpens up under the mist, from the pane-in's golden cut…
    master.to(
      contents,
      { autoAlpha: 1, y: 0, duration: config.contentInS, ease: "power2.out" },
      start + config.paneInS * config.contentAtFrac,
    );
    // …while the mist lingers, then wipes clear, finishing last.
    master.to(
      fog,
      { opacity: 0, duration: config.fogClearS, ease: "power2.inOut" },
      start + config.paneInS,
    );
    return { svg: fog, group: tile, contents: [tile, ...contents] };
  });
}

/**
 * The condensed group's own settle end, relative to its own start `at` — the last pane's content
 * sharpening (icon + label), NOT the fog-clear tail: `fogClearS` lingers well past this
 * deliberately (this module's own header — "among the last things still moving"). Mirrors
 * addGlassCondense's own per-tile math (its only other reader) so the two can't drift; exposed
 * for the entrance's leaderboard-gate re-key (game-start-entrance.js — the reveal fires once
 * the screen's furniture, not its lingering tails, has settled). `tileCount` is the caller's
 * own staged-element count (addGlassCondense's residue length) rather than a re-query, since
 * the two must agree on which elements were actually staged. `overrides` must be the exact same
 * object passed to the addGlassCondense call this mirrors (review finding: addGlassCondense
 * accepts caller overrides — `dev/glass-condense.js`'s lab tab and the result strip's own knob
 * bag both pass them — so reading GLASS_CONDENSE_DEFAULTS unconditionally would silently drift
 * the moment a call this mirrors ever passed one).
 */
export function condenseGroupSettleEnd(at, tileCount, overrides = {}) {
  if (tileCount === 0) return at;
  const { staggerS, paneInS, contentAtFrac, contentInS } = {
    ...GLASS_CONDENSE_DEFAULTS,
    ...overrides,
  };
  const lastStart = at + (tileCount - 1) * staggerS;
  return lastStart + paneInS * contentAtFrac + contentInS;
}

/**
 * Hand every condensed pane back to the stylesheet: drop the fog overlay and clear the
 * GSAP residue so each element rests as the factory render. The component's own shed —
 * the start screen's entrance is the one consumer that doesn't call it, because it merges
 * this residue with the rows group's identically-shaped one and sheds both through
 * shedTogglesEtch (addGlassCondense's own contract note). No `position` reset, unlike that
 * sibling: the condense never sets one.
 */
export function shedGlassCondense(residue) {
  for (const { svg, contents } of residue) {
    svg.remove();
    gsap.set(contents, { clearProps: "all" });
  }
}
